import { memo, useEffect, useMemo, useRef, useState } from 'react'
import { useSearchParams } from 'react-router-dom'
import { useDownloadBugReports } from '../hooks/useDownloadBugReports'
import { RefreshCw, Search } from 'lucide-react'
import { VALID_STATUSES } from '../../netlify/functions/lib/bug-statuses'
import type { Project } from '../data/projectTypes'
import useIsMobile from '../hooks/useIsMobile'
import BugTable from './BugTable'
import BugStatusFilter from './BugStatusFilter'
import BugKindFilter from './BugKindFilter'
import BugSeverityFilter from './BugSeverityFilter'
import BugEnvironmentFilter from './BugEnvironmentFilter'
import BugFilterMenu from './BugFilterMenu'
import BugListActions from './BugListActions'
import Pagination from './Pagination'
import { isIssueTrackerConnected } from '../lib/issueTracker'
import { useGetProjectBugsQuery, useGetProjectEnvironmentsQuery } from '../services/projectDataApi'
import './BugListTab.css'

interface BugListTabProps {
  projectId: string
  project: Project | null
}

export default memo(function BugListTab({ projectId, project }: BugListTabProps) {
  const [statusFilter, setStatusFilter] = useState('all')
  const [kindFilter, setKindFilter] = useState('all')
  const [hideResolved, setHideResolved] = useState(true)
  const [severitySort] = useState<'asc' | 'desc' | null>('desc')
  const [severityFilter, setSeverityFilter] = useState('all')
  const [environmentFilter, setEnvironmentFilter] = useState('all')
  const [page, setPage] = useState(1)
  const pageSize = 20
  const [search, setSearch] = useState('')
  const isMobile = useIsMobile()
  const [searchParams, setSearchParams] = useSearchParams()
  const environments = useGetProjectEnvironmentsQuery(projectId).data ?? []

  const bugsQuery = useGetProjectBugsQuery({
    projectId,
    status: statusFilter,
    hideResolved,
    severitySort,
    severity: severityFilter,
    kind: kindFilter,
    environment: environmentFilter,
    page,
    pageSize,
  }, {
    // The tab unmounts while the overview keeps polling the separate count endpoint. Always refresh
    // on entry so a cached empty page cannot outlive the badge that now reports newly filed bugs.
    refetchOnMountOrArgChange: true,
  })
  const items = bugsQuery.data?.items ?? []
  const total = bugsQuery.data?.total ?? 0
  const resolvedCount = bugsQuery.data?.resolvedCount ?? 0
  const loading = bugsQuery.isLoading || bugsQuery.isFetching
  const error = bugsQuery.error

  // Honor a `?status=` deep-link once on mount (e.g. the overview "New bugs" card → status=open),
  // then strip it so it doesn't override the user's later filter changes. The status filter itself
  // lives in Redux, so we just seed it and drop the param.
  useEffect(() => {
    const deepLinked = searchParams.get('status')
    if (deepLinked && (deepLinked === 'all' || (VALID_STATUSES as readonly string[]).includes(deepLinked))) {
      setStatusFilter(deepLinked)
    }
    if (deepLinked) {
      searchParams.delete('status')
      setSearchParams(searchParams, { replace: true })
    }
  }, [searchParams, setSearchParams])

  // Honor a `?download=all` deep-link once (the run-finished notice's "download all bug reports" link
  // points here) by firing the same all-bugs markdown export the Download button performs, then strip
  // the param so a refresh doesn't re-download. Wait for the project so the file is named after it, and
  // guard with a ref so the setSearchParams re-render can't trigger a second download.
  const downloadBugReports = useDownloadBugReports(projectId)
  const downloadFired = useRef(false)
  useEffect(() => {
    if (downloadFired.current || searchParams.get('download') !== 'all' || !project) return
    downloadFired.current = true
    void downloadBugReports()
    searchParams.delete('download')
    setSearchParams(searchParams, { replace: true })
  }, [searchParams, setSearchParams, downloadBugReports, project])

  const handlePageChange = (newPage: number) => {
    setPage(newPage)
  }

  // Search narrows the current page's rows by title/description client-side (the API paginates), so
  // it's a quick in-view filter on top of the server-side status/kind/severity controls.
  const visibleBugs = useMemo(() => {
    const q = search.trim().toLowerCase()
    if (!q) return items
    return items.filter(b =>
      b.title?.toLowerCase().includes(q) || b.description?.toLowerCase().includes(q),
    )
  }, [items, search])

  return (
    <div className="bug-list-tab" data-testid="bug-list-tab">
      <h2 className="project-tab-title bug-list-tab__title">Bugs</h2>
      <div className="bug-list-tab__toolbar">
        <div className="bug-list-tab__filters">
          <BugFilterMenu
            statusFilter={statusFilter}
            onStatusChange={(val) => { setStatusFilter(val); setPage(1) }}
            kindFilter={kindFilter}
            onKindChange={(val) => { setKindFilter(val); setPage(1) }}
            severityFilter={severityFilter}
            onSeverityChange={(val) => { setSeverityFilter(val); setPage(1) }}
            hideResolved={hideResolved}
            onHideResolvedToggle={() => { setHideResolved(!hideResolved); setPage(1) }}
            resolvedCount={resolvedCount}
            includePrimary={isMobile}
            environmentFilter={environmentFilter}
            onEnvironmentChange={(val) => { setEnvironmentFilter(val); setPage(1) }}
            environments={environments}
          />
          {!isMobile && (
            <>
              <BugKindFilter
                value={kindFilter}
                onChange={(val) => { setKindFilter(val); setPage(1) }}
              />
              <BugSeverityFilter
                value={severityFilter}
                onChange={(val) => { setSeverityFilter(val); setPage(1) }}
              />
              <BugStatusFilter
                value={statusFilter}
                onChange={(val) => { setStatusFilter(val); setPage(1) }}
              />
              <BugEnvironmentFilter
                value={environmentFilter}
                onChange={(val) => { setEnvironmentFilter(val); setPage(1) }}
                environments={environments}
              />
            </>
          )}
          <label className="bug-list-tab__search">
            <Search size={15} aria-hidden />
            <input
              type="search"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Search"
              aria-label="Search bugs"
              data-testid="bug-list-search"
            />
          </label>
        </div>
        <BugListActions
          projectId={projectId}
          statusFilter={statusFilter}
          severitySort={severitySort}
          severityFilter={severityFilter}
          kindFilter={kindFilter}
          environmentFilter={environmentFilter}
          disabled={total === 0}
          isMobile={isMobile}
          issueTrackerConnected={isIssueTrackerConnected(project)}
        />
      </div>
      {error ? (
        <div className="bug-list-tab__error" role="alert" data-testid="bug-list-error">
          <p>Failed to load bugs</p>
          <button type="button" onClick={() => void bugsQuery.refetch()} data-testid="bug-list-retry" data-analytics-event="bug-list-retry-button">
            <RefreshCw size={14} aria-hidden="true" />
            Retry
          </button>
        </div>
      ) : (
        <>
          <div className="bug-list-tab__table-wrap">
            <BugTable bugs={visibleBugs} projectId={projectId} loading={loading} hasWebhook={!!project?.webhook_url} />
          </div>
          <Pagination page={page} pageSize={pageSize} total={total} onPageChange={handlePageChange} />
        </>
      )}
    </div>
  )
})
